import {
  Document,
  Image,
  Page,
  Text,
  View,
} from '@react-pdf/renderer';
import { SERVICES_TYPES, TPE } from "Achat/Constants";
import { getStringEndingNumbers } from "Achat/Components/Utils/achatUtils";
import moment from "moment";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import logo from "Resources/ISIPAY_LOGO.png";
import { styles } from "./TicketClientStyle";
import { TICKET_ROLL_WIDTH_PT } from "./ticketClientLayoutMeasure";

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher le ticket Client
 * @category Ticket Client
 */
const SERVICE_TYPE_TO_NAME_KEY = {
  [SERVICES_TYPES.ACCES_TEMPORAIRE]: "services_name.acces",
  [SERVICES_TYPES.EAU_VIDANGE]: "services_name.eau",
  [SERVICES_TYPES.EAU_VIDANGE2]: "services_name.eau",
  //AYB 26.08.25 affichage traduit EAU_ELEC sur le ticket (plus le libelle Eau)
  [SERVICES_TYPES.EAU_ELEC]: "services_types.eau_elec",
  [SERVICES_TYPES.ELECTRICICTE]: "services_name.elec",
  [SERVICES_TYPES.A16]: "services_name.prise",
  [SERVICES_TYPES.A32]: "services_name.prise",
  [SERVICES_TYPES.A63]: "services_name.prise",
  [SERVICES_TYPES.PRISE_REHARGE]: "services_name.prise",
  [SERVICES_TYPES.PARKING]: "services_name.park",
  [SERVICES_TYPES.STATIONNEMENT_DEMONSTRATEUR]: "services_name.stationnement",
  [SERVICES_TYPES.TAXE_SEJOUR]: "services_name.tax",
  [SERVICES_TYPES.TEST]: "services_name.park",
};

const TicketClient = ((props) => {
  //#region INITIALISATION
  const {
    achatData,
    achatPayzen,
    detailAchats,
    nomSiteFallback,
    pageHeightPt,
    typePaiement,
  } = props;

  // Traduction i18n
  const { t } = useTranslation();
  //AYB 2025.11.12 Initialisation des helpers pour afficher les détails de commande sur le ticket
  const details = Array.isArray(detailAchats) ? detailAchats : [];
  const devise = achatPayzen?.devise ?? "€";
  const parseNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatCurrency = (value) => `${parseNumber(value).toFixed(2)} ${devise}`.trim();
  const formatNumber = (value) => parseNumber(value).toString();
  const getComputedDetailTotal = (detail) => parseNumber(detail?.quantite) * parseNumber(detail?.tarifService);
  const isTaxDetail = (detail) => {
    const type = detail?.idServiceType ?? detail?.serviceType;
    return type === SERVICES_TYPES.TAXE_SEJOUR;
  };
  const computedDetails = details.reduce((acc, detail) => acc + getComputedDetailTotal(detail), 0);
  const paidTotal = parseNumber(achatPayzen?.montant ?? achatData?.montant);
  //AYB 26.08.25 ticket : si palier forfait (6h payees 8€), ne pas afficher 6 x 3€ = 18€ ; utiliser le montant paye
  const isForfaitDisplay = paidTotal > 0 && computedDetails > 0 && Math.abs(computedDetails - paidTotal) > 0.009;
  const taxTotal = details.filter(isTaxDetail).reduce((acc, detail) => acc + getComputedDetailTotal(detail), 0);
  const nonTaxCount = details.filter((detail) => !isTaxDetail(detail)).length;
  const getDetailTotal = (detail) => {
    if (isForfaitDisplay && !isTaxDetail(detail) && nonTaxCount === 1) {
      return Math.round((paidTotal - taxTotal) * 100) / 100;
    }
    return getComputedDetailTotal(detail);
  };
  const totalToShow = paidTotal > 0 ? paidTotal : computedDetails;
  const hasDetails = details.length > 0;
  //AYB 2025.11.13 Détermination du nom de site si disponible
  const pickName = (value) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    const normalized = `${value}`.trim();
    return normalized.length ? normalized : undefined;
  };
  const siteName =
    pickName(achatData?.nomSite)
    ?? pickName(achatData?.siteName)
    ?? pickName(achatData?.nomTerminal)
    ?? pickName(achatPayzen?.nomSite)
    ?? pickName(achatPayzen?.siteName)
    ?? pickName(achatPayzen?.nomTerminal)
    ?? pickName(nomSiteFallback)
    ?? "";
  //AYB 2025.11.13 Support des différentes casses pour le code d'accès
  const codeAcces =
    achatData?.codeAcces
    ?? achatData?.CodeAcces
    ?? detailAchats?.[0]?.codeAcces
    ?? detailAchats?.[0]?.CodeAcces
    ?? achatPayzen?.codeAcces
    ?? achatPayzen?.CodeAcces
    ?? "";
  //AYB 2025.11.13 Normalisation du code d'accès (gestion des chaînes vides)
  const codeAccesValue = typeof codeAcces === "string" ? codeAcces.trim() : `${codeAcces}`.trim();

  const pageSize = [TICKET_ROLL_WIDTH_PT, pageHeightPt];
  // #endregion

  //#region INTERFACE
  const renderInfoRow = (label, value) => {
    if (!value) {
      return null;
    }

    return (
      <View style={styles.TicketPayzen_InfoRow}>
        <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_InfoLabel]}>
          {label}
        </Text>
        <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_InfoValue]}>
          {value}
        </Text>
      </View>
    );
  };

  const purchaseDate = achatPayzen?.date ?? achatPayzen?.dateAchat ?? achatPayzen?.dateCreation;
  const formattedDate = purchaseDate ? moment(purchaseDate).local().format("DD/MM/YYYY HH:mm") : "-";
  const contactEmail = achatPayzen?.adresseMail ?? achatData?.adresseMail;
  const codeDisplay = codeAccesValue || t("ticket.codeNotRequired");
  const immatriculationDisplay = achatData?.immatriculation ?? achatPayzen?.immatriculation ?? achatPayzen?.Immatriculation ?? "";

  const getServiceLabel = (detail) => {
    const inferredType =
      detail?.serviceParent?.idServiceType
      ?? detail?.serviceTypeMaitre
      ?? detail?.idServiceType;

    const translationKey = inferredType ? SERVICE_TYPE_TO_NAME_KEY[inferredType] : undefined;
    if (translationKey) {
      const suffix = detail?.nomService ? getStringEndingNumbers(detail.nomService) : null;
      const localized = t(translationKey);
      return suffix ? `${localized} ${suffix}` : localized;
    }
    if (detail?.nomService) return detail.nomService;
    if (detail?.idService) return `${t("ticket.service") || "Service"} ${detail.idService}`;
    return "-";
  };
  const getTicketLine = (detail) => {
    const line =
      detail?.tarif?.ligneTicket
      ?? detail?.tarif?.lineTicket
      ?? detail?.ligneTicket
      ?? detail?.lineTicket;

    if (line === null || line === undefined) {
      return null;
    }

    const normalized = `${line}`.trim();
    return normalized.length ? normalized : null;
  };
  const ticketLines = details.map(getTicketLine).filter(Boolean);

  const renderDetails = () => {
    if (!hasDetails) {
      return null;
    }

    return (
      <View style={styles.TicketPayzen_DetailCard}>
        <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_DetailHeading]}>
          {t("ticket.detailsTitle")}
        </Text>
        <View style={styles.TicketPayzen_DetailList}>
          {details.map((detail, index) => {
            const key = detail?.["@id"] ?? `${detail?.idService ?? "detail"}-${index}`;
            const serviceLabel = getServiceLabel(detail);
            const qty = formatNumber(detail?.quantite);
            const price = formatCurrency(detail?.tarifService);
            const lineTotal = formatCurrency(getDetailTotal(detail));
            const showUnitPrice = !(isForfaitDisplay && !isTaxDetail(detail) && nonTaxCount === 1);

            return (
              <View key={key} style={styles.TicketPayzen_DetailRow}>
                <View style={styles.TicketPayzen_DetailInfo}>
                  <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_DetailService]}>
                    {serviceLabel}
                    <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_DetailMeta]}>
                      {showUnitPrice ? ` • ${qty} x ${price}` : ` • ${qty}`}
                    </Text>
                  </Text>
                </View>
                <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_DetailAmount]}>
                  {lineTotal}
                </Text>
              </View>
            );
          })}
        </View>
        <View style={styles.TicketPayzen_DetailFooter}>
          <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_TextEmphasis, styles.TicketPayzen_TotalLabel]}>
            {t("ticket.totalLabel")}
          </Text>
          <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_TextEmphasis]}>
            {formatCurrency(totalToShow)}
          </Text>
        </View>
      </View>
    );
  };

  const getTicket = () => {
    if (typePaiement === TPE) {
      return (
        <View style={styles.TicketPayzen_Section}>
          <Text style={styles.TicketPayzen_PrimaryText}>
            {achatPayzen}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.TicketPayzen_Content}>
        <View style={styles.TicketPayzen_Section}>

          {renderInfoRow(t("ticket.site"), siteName)}
          {renderInfoRow(t("ticket.dateBuy"), formattedDate)}
          {renderInfoRow(t("ticket.order"), achatPayzen?.transactionIdPayzen)}
          {renderInfoRow(t("ticket.cardNumber"), achatPayzen?.numeroCarte)}
          {renderInfoRow(t("ticket.mail"), contactEmail)}
        </View>

        {renderDetails()}

        {(immatriculationDisplay || codeAccesValue) && (
          <View style={styles.TicketPayzen_Section}>
            {renderInfoRow(t("ticket.immatriculation"), immatriculationDisplay || "-")}
            <View style={styles.TicketPayzen_CodeBlock}>
              <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_CodeLabel]}>
                {t("ticket.code")}
              </Text>
              <Text style={styles.TicketPayzen_CodeValue}>
                {codeDisplay}
              </Text>
            </View>
          </View>
        )}

        {ticketLines.length > 0 && (
          <View style={styles.TicketPayzen_Section}>
            {ticketLines.map((line, index) => (
              <Text
                key={`${line}-${index}`}
                style={[styles.TicketPayzen_Text, styles.TicketPayzen_InfoText]}
              >
                {line}
              </Text>
            ))}
          </View>
        )}

        <View style={styles.TicketPayzen_MailInfoSeparator} />
        <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_MailInfoText]}>
          {t("ticket.mailInfo")}
        </Text>
      </View>
    );
  };

  // Interface
  if (!achatPayzen) {
    return null;
  }

  return (
    <Document>
      <Page
        size={pageSize}
        style={styles.TicketPayzen_Page}
        wrap={false}
      >
        <View style={styles.TicketPayzen_Header}>
          <Image
            src={logo}
            style={styles.TicketPayzen_Image}
          />
          <Text style={[styles.TicketPayzen_Text, styles.TicketPayzen_HeaderTitle]}>
            {t("ticket.headerTitle")}
          </Text>
        </View>
        {getTicket()}
      </Page>
    </Document >
  )
  // #endregion
});
// #endregion

// #region PROPRIETES
/**
 * Type des propriétés de {@link TicketClient}
 * @typedef {Object} TicketClient.propTypes
 * @property {object} [achatPayzen=null] Objet contenant l'achat effectué ou le ticket client
 * @property {object} [achatData={}] Objet contenant le retour de l'achat effectué ( pour le code accès )
 * @property {Array} [detailAchats=[]] Liste des détails de l'achat
 * @property {string} [nomSiteFallback] Nom du site si absent des données API (ex: borne.name)
 * @property {number} pageHeightPt Hauteur de la page PDF en points (une seule page, adaptée au contenu)
 * @property {string} [typePaiement=null]
*/
TicketClient.propTypes = {
  achatPayzen: PropTypes.object,
  achatData: PropTypes.object,
  detailAchats: PropTypes.array,
  nomSiteFallback: PropTypes.string,
  pageHeightPt: PropTypes.number.isRequired,
  typePaiement: PropTypes.string,
};

// Valeurs par défaut des propriétés
TicketClient.defaultProps = {
  achatPayzen: null,
  achatData: {},
  detailAchats: [],
  nomSiteFallback: null,
  typePaiement: null
};
// #endregion

export { TicketClient };

